import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { isFulfilledAction, isPendingAction, isRejectedAction } from '../utils';
import { asyncPatientsActions } from './thunks';
import {
  AttachmentMessageContent,
  FileType,
  GetMessagesAttachmentsRequestParams,
  GetPatientCasesParams,
  GetPatientMessagesRequestParams,
  MessagesType,
  Patient,
  PatientCase,
  PatientListInfo,
  PatientMessagesInfo,
} from 'types';
import { dateNowUTC } from 'utils';

export type AsyncPatientAction = keyof typeof asyncPatientsActions;

type AsyncActionsAreLoading = {
  [key in AsyncPatientAction]: boolean;
};

export interface PatientsState {
  patients: Patient[];
  patientListInfo: PatientListInfo | null;
  currentPatientCase: PatientCase | null;
  patientPageFilters: GetPatientCasesParams;
  patientCasesCount: number | null;
  patientsPageTimestamp: string | null;
  asyncActionsAreLoading: AsyncActionsAreLoading;
  activityFeed: Patient[];
  activityFeedInfo: PatientListInfo | null;
  activityFeedFilter: GetPatientCasesParams;
  dashboardNewCases: {
    patients: Patient[];
    patientListInfo: PatientListInfo | null;
  };
  dashboardLetterCases: {
    patients: Patient[];
    patientListInfo: PatientListInfo | null;
  };
  dashboardInTreatmentCases: {
    patients: Patient[];
    patientListInfo: PatientListInfo | null;
  };
  dashboardFinishedCases: {
    patients: Patient[];
    patientListInfo: PatientListInfo | null;
  };
  patientMessage: MessagesType[];
  patientMessageInfo: PatientMessagesInfo | null;
  patientMessageFilters: GetPatientMessagesRequestParams;
  messageAttachments: AttachmentMessageContent[];
  messageAttachmentsInfo: PatientMessagesInfo | null;
  messageAttachmentsRequestParams: GetMessagesAttachmentsRequestParams;
  lastReadMessageId: number | null;
  shouldMessagesPageUpdate: boolean;
  shouldPatientPageUpdate: boolean;
  shouldMessagesUpdate: boolean;
  isPatientPageEdited?: boolean;
}

const initialLoadingsState: AsyncActionsAreLoading = {
  ...Object.keys(asyncPatientsActions).reduce((acc, actionType) => {
    acc[actionType as AsyncPatientAction] = false;
    return acc;
  }, {} as AsyncActionsAreLoading),
  getPatientCases: true,
  getPatientCase: true,
  getPatientMessages: true,
};

export const initialState: PatientsState = {
  patients: [],
  patientListInfo: null,
  currentPatientCase: null,
  patientPageFilters: {
    isArchived: false,
  },
  patientCasesCount: null,
  patientsPageTimestamp: null,
  asyncActionsAreLoading: initialLoadingsState,
  activityFeed: [],
  activityFeedInfo: null,
  activityFeedFilter: {},
  dashboardNewCases: {
    patients: [],
    patientListInfo: null,
  },
  dashboardLetterCases: {
    patients: [],
    patientListInfo: null,
  },
  dashboardInTreatmentCases: {
    patients: [],
    patientListInfo: null,
  },
  dashboardFinishedCases: {
    patients: [],
    patientListInfo: null,
  },
  patientMessage: [],
  patientMessageInfo: null,
  patientMessageFilters: {},
  messageAttachments: [],
  messageAttachmentsInfo: null,
  messageAttachmentsRequestParams: {
    fileTypes: [FileType.BMP, FileType.JPEG, FileType.JPG, FileType.PNG, FileType.WEBP],
  },
  lastReadMessageId: null,
  shouldMessagesPageUpdate: true,
  shouldPatientPageUpdate: true,
  shouldMessagesUpdate: true,
  isPatientPageEdited: false,
};

export const slice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    setCurrenPatientCase: (state, { payload }: PayloadAction<PatientCase | null>) => {
      state.currentPatientCase = payload;
    },
    setPatientPageFilters: (state, { payload }: PayloadAction<GetPatientCasesParams>) => {
      state.patientPageFilters = payload;
    },
    setActivityFeedFilter: (state, { payload }: PayloadAction<GetPatientCasesParams>) => {
      state.activityFeedFilter = payload;
    },
    setPatientPageTimestamp: (state, { payload }: PayloadAction<string | undefined>) => {
      if (!payload) {
        state.patientsPageTimestamp = dateNowUTC();
        return;
      }
      state.patientsPageTimestamp = payload;
    },
    setPatientMessageFilters: (state, { payload }: PayloadAction<GetPatientMessagesRequestParams>) => {
      state.patientMessageFilters = payload;
    },
    setMessageAttachmentsRequestParams: (state, { payload }: PayloadAction<GetMessagesAttachmentsRequestParams>) => {
      state.messageAttachmentsRequestParams = payload;
    },
    resetPatientMessages: (state) => {
      state.patientMessage = [];
      state.patientMessageInfo = null;
      state.patientMessageFilters = initialState.patientMessageFilters;
    },
    resetPatientCasesBeforeUpdate: (state) => {
      state.patientListInfo = null;
      state.patients = [];
      state.patientPageFilters.page = 0;
    },
    resetActivityFeedBeforeUpdate: (state) => {
      state.activityFeed = [];
      state.activityFeedInfo = null;
      state.activityFeedFilter.page = 0;
    },
    setLastReadMessage: (state, { payload }: PayloadAction<number>) => {
      state.lastReadMessageId = payload;
    },
    resetMessagesBeforeUpdate: (state) => {
      state.patientMessageFilters.page = 0;
      state.patientMessageInfo = null;
      state.patientMessage = [];
    },
    setShouldMessagesPageUpdate: (state, { payload }: PayloadAction<boolean>) => {
      state.shouldMessagesPageUpdate = payload;
    },
    setShouldPatientPageUpdate: (state, { payload }: PayloadAction<boolean>) => {
      state.shouldPatientPageUpdate = payload;
    },
    setShouldMessagesUpdate: (state, { payload }: PayloadAction<boolean>) => {
      state.shouldMessagesUpdate = payload;
    },
    setIsPatientPageEdited: (state, { payload }: PayloadAction<boolean>) => {
      state.isPatientPageEdited = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(asyncPatientsActions.getPatientCases.fulfilled, (state, { payload }) => {
      const { content, ...info } = payload;

      if (info.empty) {
        state.asyncActionsAreLoading.getPatientCase = false;
      }

      if (!info.first) {
        state.patients = [...state.patients, ...content];
        state.patientListInfo = info;
        return;
      }
      state.patients = content;
      state.patientListInfo = info;
    });
    builder.addCase(asyncPatientsActions.getPatientCase.fulfilled, (state, { payload }) => {
      if (state.currentPatientCase && state.currentPatientCase?.id !== payload.id) {
        state.lastReadMessageId = null;
      }
      state.currentPatientCase = payload;
    });
    builder.addCase(asyncPatientsActions.getPatientCase.rejected, (state) => {
      state.currentPatientCase = null;
    });
    builder.addCase(asyncPatientsActions.updatePatientCase.fulfilled, (state, { payload }) => {
      state.currentPatientCase = payload;
    });
    builder.addCase(asyncPatientsActions.getPatientCaseCount.fulfilled, (state, { payload }) => {
      state.patientCasesCount = payload.count;
    });
    builder.addCase(asyncPatientsActions.assignPatientCaseToDoctor.fulfilled, (state, { payload }) => {
      state.currentPatientCase = payload;
    });
    builder.addCase(asyncPatientsActions.removeAssignmentFromCase.fulfilled, (state, { payload }) => {
      state.currentPatientCase = payload;
    });
    builder.addCase(asyncPatientsActions.updatePatientDiagnosisAndTreatment.fulfilled, (state, { payload }) => {
      state.currentPatientCase = payload;
    });
    builder.addCase(asyncPatientsActions.updatePatientAlignerPlan.fulfilled, (state, { payload }) => {
      state.currentPatientCase = payload;
    });
    builder.addCase(asyncPatientsActions.getActivityFeed.fulfilled, (state, { payload }) => {
      const { content, ...info } = payload;

      if (!info.first) {
        state.activityFeed = [...state.activityFeed, ...content];
        state.activityFeedInfo = info;
        return;
      }
      state.activityFeed = content;
      state.activityFeedInfo = info;
    });
    builder.addCase(asyncPatientsActions.getPatientMessages.fulfilled, (state, { payload }) => {
      const { content, ...info } = payload;

      if (!info.first) {
        state.patientMessage = [...state.patientMessage, ...content];
        state.patientMessageInfo = info;
        return;
      }
      state.patientMessage = content;
      state.patientMessageInfo = info;
    });
    builder.addCase(asyncPatientsActions.getMessagesAttachments.fulfilled, (state, { payload }) => {
      const { content, ...info } = payload;

      if (!info.first) {
        state.messageAttachments = [...state.messageAttachments, ...content];
        state.messageAttachmentsInfo = info;
        return;
      }
      state.messageAttachments = content;
      state.messageAttachmentsInfo = info;
    });
    builder.addCase(asyncPatientsActions.setPatientMessagesAfterReactions, (state, { payload }) => {
      state.patientMessage = payload;
    });
    builder.addMatcher(isPendingAction, (state, action) => {
      const actionName = action.type.split('/')[1] as AsyncPatientAction;
      state.asyncActionsAreLoading[actionName] = true;
    });
    builder.addMatcher(isFulfilledAction, (state, action) => {
      const actionName = action.type.split('/')[1] as AsyncPatientAction;
      state.asyncActionsAreLoading[actionName] = false;
    });
    builder.addMatcher(isRejectedAction, (state, action) => {
      const actionName = action.type.split('/')[1] as AsyncPatientAction;
      state.asyncActionsAreLoading[actionName] = false;
    });
  },
});

export const patientsActions = { ...slice.actions, ...asyncPatientsActions };
export const patientsReducer = slice.reducer;
